import { Link, Typography } from "@npco/zeller-design-system";
import * as Icon from "@npco/zeller-design-system/icon";
import type { ReactNode } from "react";
import { twJoin } from "tailwind-merge";

import type { ConnectionCheck, HopStatus } from "../use-connection-status";

const RAIL_COLOUR: Record<HopStatus, string> = {
	connected: "bg-green-1000 dark:bg-green-700",
	disconnected: "bg-red-1000 dark:bg-red-700",
	checking: "bg-grey-250 dark:bg-grey-600",
};

const LABEL: Record<HopStatus, string> = {
	connected: "Connected",
	disconnected: "Not Connected",
	checking: "Checking",
};

function StatusIcon({ status }: Readonly<{ status: HopStatus }>) {
	if (status === "connected") {
		return (
			<Icon.CheckmarkFilled
				width={16}
				height={16}
				className="text-green-1000 dark:text-green-700"
			/>
		);
	}
	if (status === "disconnected") {
		return (
			<Icon.CrossmarkFilled
				width={16}
				height={16}
				className="text-red-1000 dark:text-red-700"
			/>
		);
	}
	return (
		<span className="block size-[14px] rounded-full border-2 border-grey-250 dark:border-grey-600" />
	);
}

function Row({
	icon,
	title,
	right,
	rail,
}: Readonly<{
	icon: ReactNode;
	title: string;
	right?: string;
	/** Colour of the rail leading into this row from the one above. */
	rail?: HopStatus;
}>) {
	return (
		<li className="relative flex h-[40px] flex-row items-center gap-12">
			{rail && (
				<span
					aria-hidden
					className={twJoin(
						"absolute -top-1/2 left-[7px] h-full w-[2px]",
						RAIL_COLOUR[rail],
					)}
				/>
			)}
			<span className="relative z-10 flex size-16 items-center justify-center rounded-full bg-white dark:bg-grey-950">
				{icon}
			</span>
			<span className="flex-1 text-sm">{title}</span>
			{right && (
				<span className="text-sm text-grey-550 dark:text-grey-400">
					{right}
				</span>
			)}
		</li>
	);
}

/** Every hop after a disconnected one is unreachable from here too. */
function cascade({ internet, cloud, terminal }: ConnectionCheck) {
	const cloudShown: HopStatus =
		internet === "disconnected" ? "disconnected" : cloud;
	const terminalShown: HopStatus =
		cloudShown === "disconnected" ? "disconnected" : terminal;
	return { internet, cloud: cloudShown, terminal: terminalShown };
}

export function ConnectionCard({
	terminalName,
	connection,
	disabled,
	onUnpair,
}: Readonly<{
	terminalName: string;
	connection: ConnectionCheck;
	disabled: boolean;
	onUnpair: () => void;
}>) {
	const { internet, cloud, terminal } = cascade(connection);
	// The first failed hop is the one to fix.
	let failed: "pos" | "terminal" | undefined;
	if (internet === "disconnected" || cloud === "disconnected") {
		failed = "pos";
	} else if (terminal === "disconnected") {
		failed = "terminal";
	}
	const checking = [internet, cloud, terminal].includes("checking");

	return (
		<section className="flex flex-col gap-12">
			<Typography as="h2" variant="body-base">
				Paired to this POS System
			</Typography>
			<div
				className={twJoin(
					"flex flex-col gap-8 rounded-8 border p-16",
					"border-grey-90 dark:border-grey-800",
				)}
			>
				<div className="flex h-[40px] flex-row items-center gap-12">
					<Icon.Terminal
						width={16}
						height={16}
						className="text-blue-1000 dark:text-blue-400"
					/>
					<span className="flex-1 text-sm">{terminalName}</span>
					<Link label="Unpair" onClick={onUnpair} disabled={disabled} />
				</div>
				<ol className="flex flex-col pl-28">
					<Row icon={<Icon.Sdk width={16} height={16} />} title="POS System" />
					<Row
						icon={<StatusIcon status={internet} />}
						title="Internet"
						right={LABEL[internet]}
						rail={internet}
					/>
					<Row
						icon={<StatusIcon status={cloud} />}
						title="Zeller Cloud"
						right={LABEL[cloud]}
						rail={cloud}
					/>
					<Row
						icon={<StatusIcon status={terminal} />}
						title="Zeller Terminal"
						right={LABEL[terminal]}
						rail={terminal}
					/>
				</ol>
				{failed && (
					<div className="flex flex-col gap-16 pt-8 text-sm">
						<div>
							<p>To resolve this issue,</p>
							<ol className="list-decimal pl-20">
								{failed === "pos" ? (
									<>
										<li>Ensure your POS is connected to the internet</li>
										<li>Ensure your POS is paired to a Zeller Terminal</li>
									</>
								) : (
									<>
										<li>
											Ensure your Zeller Terminal is connected to the internet
										</li>
										<li>Ensure your Zeller Terminal is paired to this POS</li>
									</>
								)}
							</ol>
						</div>
						<div>
							<Link
								label="Check Connection"
								onClick={connection.check}
								disabled={disabled || checking}
							/>
						</div>
					</div>
				)}
			</div>
		</section>
	);
}
